import { SidebarSimple, UploadSimple } from '@phosphor-icons/react';
import { useListWorkspaces } from '@reelplicate/api-client/workspaces';
import {
  Button,
  Field,
  IconButton,
  Modal,
  ModalClose,
  ModalContent,
  TextInput,
  toast,
} from '@reelplicate/ui';
import { type DragEvent, useEffect, useRef, useState } from 'react';
import { Outlet, useLocation, useMatch, useNavigate } from 'react-router';

import { CaptionModal } from './caption-modal';
import { ImportLinkModal } from './import-link-modal';
import { LibraryPanel } from './library-panel';
import { useMedia } from './media-context';
import { type CaptionPrefill, type MediaOutlet, useMediaOutlet } from './media-outlet';
import { MediaProvider } from './media-store';
import { RuleBuilderModal, type RuleBuilderTarget } from './rule-builder-modal';
import { UploadQueue } from './upload-queue';

/** "Show library" when the panel is hidden, for page headers. */
export function ShowLibraryButton() {
  const { panelHidden, showPanel } = useMediaOutlet();
  if (!panelHidden) return null;
  return (
    <IconButton
      icon={SidebarSimple}
      variant="outline"
      aria-label="Show library"
      onClick={showPanel}
    />
  );
}

/**
 * The Media section's frame: the Library panel beside the page, drag-and-drop
 * upload anywhere over the page (Figma "Drop target", 191:2761), the upload
 * queue and the "New collection" dialog.
 */
export function MediaLayout() {
  return (
    <MediaProvider>
      <MediaFrame />
    </MediaProvider>
  );
}

const hasFiles = (e: DragEvent) => e.dataTransfer.types.includes('Files');

/** Navigation state that asks the Media section to start something on arrival. */
export type MediaIntent = { mediaIntent: 'upload' | 'new-collection' | 'add-from-link' };

const intentOf = (state: unknown) => (state as Partial<MediaIntent> | null)?.mediaIntent;

function MediaFrame() {
  const { upload, collections } = useMedia();
  const [panelOpen, setPanelOpen] = useState(true);
  const [creating, setCreating] = useState(false);
  // The smart collection builder: closed, or what it's building.
  const [building, setBuilding] = useState<RuleBuilderTarget | null>(null);
  // "New caption": closed, or open (maybe into a collection).
  const [captioning, setCaptioning] = useState<CaptionPrefill | null>(null);
  // "Add from link": closed, or open (maybe into a collection).
  const [linking, setLinking] = useState<{ collectionId?: string } | null>(null);
  const { data: workspaces } = useListWorkspaces();
  const [dragging, setDragging] = useState(0);
  const depth = useRef(0);
  const input = useRef<HTMLInputElement>(null);
  // The collection the open file picker uploads into, if any.
  const pickingInto = useRef<string | undefined>(undefined);
  // Files dropped on a hand-picked collection's page go into it.
  const onCollection = useMatch('/media/collections/:collectionId')?.params.collectionId;
  const dropTarget = collections.find((c) => c.id === onCollection && !c.smart);

  const start = (files: File[], collectionId?: string) => {
    if (files.length === 0) return;
    upload(files, { collectionId });
    toast(`Uploading ${files.length} ${files.length === 1 ? 'file' : 'files'}`);
  };

  const context: MediaOutlet = {
    newCollection: () => setCreating(true),
    editSmartCollection: (target = {}) => setBuilding(target),
    newCaption: (prefill = {}) => setCaptioning(prefill),
    addFromLink: (collectionId) => setLinking({ collectionId }),
    pickFiles: (collectionId) => {
      pickingInto.current = collectionId;
      input.current?.click();
    },
    panelHidden: !panelOpen,
    showPanel: () => setPanelOpen(true),
  };

  // Run a command-palette intent once, then drop it so Back doesn't repeat it.
  const location = useLocation();
  const navigate = useNavigate();
  const intent = intentOf(location.state);
  if (intent === 'new-collection' && !creating) setCreating(true);
  if (intent === 'add-from-link' && !linking) setLinking({});
  useEffect(() => {
    if (!intent) return;
    if (intent === 'upload') {
      pickingInto.current = undefined;
      input.current?.click();
    }
    void navigate(`${location.pathname}${location.search}`, {
      replace: true,
      state: null,
    });
  }, [intent, location.pathname, location.search, navigate]);

  return (
    <div className="flex h-full">
      {panelOpen && (
        <LibraryPanel
          onCollapse={() => setPanelOpen(false)}
          onNewCollection={context.newCollection}
          onNewSmartCollection={() => context.editSmartCollection()}
        />
      )}
      <div
        className="relative flex min-w-0 flex-1 flex-col"
        onDragEnter={(e) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          depth.current += 1;
          setDragging(e.dataTransfer.items.length);
        }}
        onDragOver={(e) => {
          if (hasFiles(e)) e.preventDefault();
        }}
        onDragLeave={() => {
          depth.current = Math.max(0, depth.current - 1);
          if (depth.current === 0) setDragging(0);
        }}
        onDrop={(e) => {
          if (!hasFiles(e)) return;
          e.preventDefault();
          depth.current = 0;
          setDragging(0);
          start([...e.dataTransfer.files], dropTarget?.id);
        }}
      >
        <div className="flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto p-8 *:shrink-0">
          <Outlet context={context} />
        </div>
        {dragging > 0 && (
          <div
            aria-hidden
            className="bg-brand-subtle/90 border-brand-primary pointer-events-none absolute inset-8 z-20 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed"
          >
            <span className="bg-brand-primary flex size-14 items-center justify-center rounded-2xl">
              <UploadSimple className="text-text-on-brand size-7" />
            </span>
            <p className="text-heading-h4 text-text-primary">
              Drop {dragging} {dragging === 1 ? 'file' : 'files'}{' '}
              {dropTarget ? `to add to “${dropTarget.name}”` : 'to upload'}
            </p>
            <p className="text-body-sm text-text-secondary">
              Video and audio up to 500 MB, images up to 20 MB
            </p>
          </div>
        )}
      </div>

      <input
        ref={input}
        type="file"
        multiple
        accept="video/*,image/*,audio/*"
        aria-label="Upload files"
        className="sr-only"
        tabIndex={-1}
        onChange={(e) => {
          start([...(e.target.files ?? [])], pickingInto.current);
          e.target.value = '';
        }}
      />
      <UploadQueue />
      <NewCollectionModal open={creating} onOpenChange={setCreating} />
      <RuleBuilderModal target={building} onClose={() => setBuilding(null)} />
      <CaptionModal
        workspaceId={workspaces?.[0]?.id ?? ''}
        open={captioning !== null}
        collectionId={captioning?.collectionId}
        caption={captioning?.caption}
        title={captioning?.title}
        source={captioning?.source}
        onOpenChange={(open) => !open && setCaptioning(null)}
      />
      <ImportLinkModal
        workspaceId={workspaces?.[0]?.id ?? ''}
        open={linking !== null}
        collectionId={linking?.collectionId}
        onOpenChange={(open) => !open && setLinking(null)}
      />
    </div>
  );
}

function NewCollectionModal({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { createCollection } = useMedia();
  const navigate = useNavigate();
  const [name, setName] = useState('');

  const [saving, setSaving] = useState(false);

  const create = async () => {
    setSaving(true);
    const created = await createCollection(name.trim(), {});
    setSaving(false);
    if (!created) return;
    onOpenChange(false);
    setName('');
    void navigate(`/media/collections/${created.id}`);
  };

  return (
    <Modal
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) setName('');
      }}
    >
      <ModalContent
        size="sm"
        title="New collection"
        description="Hand-pick reels, clips and captions for a campaign. For one that fills itself, add a smart collection."
        footer={
          <>
            <ModalClose asChild>
              <Button variant="ghost">Cancel</Button>
            </ModalClose>
            <Button
              disabled={!name.trim()}
              loading={saving}
              onClick={() => void create()}
            >
              Create collection
            </Button>
          </>
        }
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) void create();
          }}
        >
          <Field label="Name">
            <TextInput
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Spring launch"
              autoComplete="off"
            />
          </Field>
        </form>
      </ModalContent>
    </Modal>
  );
}
